import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Sparkles, Lock, Info } from 'lucide-react';
import { toast } from 'sonner';
import StripeCheckout from '@/components/StripeCheckout';
import BetaBanner from '@/components/BetaBanner';
import { usePaymentsConfig } from '@/lib/usePaymentsConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_ORDER = ['daily', 'monthly', 'quarterly', 'annual'];

const formatPeriodEnd = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
};

/**
 * SubscriptionPaywall
 * Shown to sellers who don't have an active subscription. 4 plans (daily,
 * monthly, quarterly, annual). Non-daily plans align to natural calendar
 * periods and prorate the first charge based on days remaining. The daily
 * plan grants 24h from purchase without proration.
 * On subscribe, we call POST /api/payments/checkout which returns a Stripe
 * Checkout URL — the user is redirected there.
 */
const SubscriptionPaywall = ({ onSubscribed }) => {
  const { getAuthHeader, refreshUser, user } = useAuth();
  const payCfg = usePaymentsConfig();
  const [testMode, setTestMode] = useState(false);
  const [plans, setPlans] = useState({});
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/subscriptions/plans`, { headers: { Authorization: getAuthHeader() } })
      .then((res) => setPlans(res.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = plans[selectedPlan];
  const preview = current?.preview;

  // Opens the embedded Stripe PaymentElement (methods come from the Dashboard config)
  const handleSubscribe = () => setCheckoutOpen(true);

  const handlePaid = async (intentId) => {
    setLoading(true);
    try {
      for (let i = 0; i < 8; i += 1) {
        const r = await axios.get(`${API}/payments/status/${intentId}`);
        if (r.data.payment_status === 'paid') break;
        await new Promise((res) => setTimeout(res, 1500));
      }
      toast.success('¡Pago completado! Suscripción activada.');
      await refreshUser?.();
      onSubscribed?.();
    } catch (error) {
      toast.error('Pago recibido; la activación puede tardar unos segundos.');
    } finally {
      setLoading(false);
    }
  };

  if (!payCfg.payments_enabled && !testMode) {
    return (
      <div className="p-6 space-y-4" data-testid="paywall-beta">
        <BetaBanner />
        <p className="text-xs text-slate-500">Durante la beta, todos los perfiles (compradores y vendedores) disfrutan de acceso completo: pujas, excedentes, contacto y chat sin suscripción.</p>
        {payCfg.test_payments_allowed && user?.is_admin && (
          <button type="button" data-testid="paywall-enable-test-mode" onClick={() => setTestMode(true)} className="text-xs text-slate-400 underline">Abrir Stripe en modo test (solo administradores / desarrollo)</button>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white" data-testid="subscription-paywall">
      <div className="p-6 pb-4 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={20} className="text-yellow-300" />
          <h2 className="text-xl font-bold">Activa tu suscripción</h2>
        </div>
        <p className="text-sm text-emerald-100">
          Publica excedentes, revela tus datos de contacto y puja en subastas ilimitadas.
        </p>
      </div>

      <div className="p-6 space-y-5">
        {/* Plan grid */}
        <div className="grid grid-cols-2 gap-2" data-testid="plan-grid">
          {PLAN_ORDER.map((key) => {
            const p = plans[key];
            if (!p) return null;
            const active = selectedPlan === key;
            const prev = p.preview;
            const showProrated = prev && prev.is_prorated;
            return (
              <button
                key={key}
                type="button"
                data-testid={`paywall-plan-${key}`}
                onClick={() => setSelectedPlan(key)}
                className={`p-3 rounded-lg border-2 text-left transition-all duration-200 ${
                  active ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{p.label}</p>
                <p className="text-lg font-bold text-slate-900 mt-1">€{p.full_price_eur.toFixed(2)}</p>
                <p className="text-xs text-slate-500">
                  {key === 'daily' ? 'por 24h' :
                   key === 'monthly' ? 'por mes' :
                   key === 'quarterly' ? 'por trimestre' : 'por año'}
                </p>
                {showProrated && (
                  <p className="text-[11px] text-emerald-700 mt-1 font-medium">
                    Ahora: €{(prev.prorated_amount_cents / 100).toFixed(2)}
                  </p>
                )}
              </button>
            );
          })}
        </div>

        {/* Proration explanation */}
        {preview && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 space-y-1" data-testid="proration-preview">
            {preview.is_prorated ? (
              <>
                <div className="flex items-start gap-2">
                  <Info size={14} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <p><strong>Prorrateo aplicado.</strong> Se te cobra la parte proporcional del periodo natural en curso.</p>
                    <p className="mt-1">
                      {preview.days_remaining} de {preview.days_in_period} días · acceso hasta el <strong>{formatPeriodEnd(preview.period_end)}</strong>.
                    </p>
                    <p className="mt-1 text-blue-700">Renovación automática con la tarifa completa al inicio del siguiente periodo.</p>
                  </div>
                </div>
              </>
            ) : selectedPlan === 'daily' ? (
              <div className="flex items-start gap-2">
                <Info size={14} className="mt-0.5 flex-shrink-0" />
                <p>Acceso durante <strong>24 horas exactas</strong> desde el momento del pago. Sin prorrateo, sin renovación automática.</p>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <Info size={14} className="mt-0.5 flex-shrink-0" />
                <p>Acceso hasta el <strong>{formatPeriodEnd(preview.period_end)}</strong> (periodo completo).</p>
              </div>
            )}
          </div>
        )}

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-center gap-2 text-xs text-slate-600">
          <Lock size={14} />
          <span>Pago seguro procesado por Stripe. Cancela cuando quieras y mantén el acceso hasta el fin del periodo.</span>
        </div>

        {checkoutOpen && preview ? (
          <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="paywall-embedded-checkout">
            <StripeCheckout
              plan={selectedPlan}
              amountLabel={`€${(preview.prorated_amount_cents / 100).toFixed(2)}`}
              onPaid={handlePaid}
              onError={(msg) => { toast.error(msg); setCheckoutOpen(false); }}
            />
            <button type="button" onClick={() => setCheckoutOpen(false)} className="mt-3 text-xs text-slate-500 underline w-full text-center" data-testid="paywall-change-plan">Cambiar de plan</button>
          </div>
        ) : (
          <Button
            onClick={handleSubscribe}
            disabled={loading || !preview}
            data-testid="paywall-subscribe-button"
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-base font-medium"
          >
            {loading ? 'Activando suscripción…' :
              preview ? `Continuar al pago · €${(preview.prorated_amount_cents / 100).toFixed(2)}` :
              'Cargando…'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPaywall;
